import { css } from 'aphrodite';
import * as React from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { AIChatRole } from '../../../../../../extViewmodels';
import { useAIConversationChatMessageQuery } from '../../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { AIMarkdownResponse } from '../../../../../../web/components-v2/ai/AiMarkdownResponse';
import { Button } from '../../../../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../../../web/styles/colors';
import { AidaDraggable } from '../../../../../components/AidaDraggable';
import { DraggableIcon } from '../../../../../components/svgs/icons/audio/DraggableIcon';
import { AnalysisPhase, IndividualAnalysis } from '../../hooks/types';
import { useMultiDemoAnalysis } from '../../hooks/useMultiDemoAnalysis';
import { CollapsibleAnalysisItem, CollapsibleSection } from '../CollapsibleSection';
import { styleSheet } from './styles';

interface DemoAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	isLoading: boolean;
	isCreatingConversation: boolean;
	onSendFollowUp: (question: string) => void;
	conversationId: string;
	aiPromptMutation: ReturnType<typeof useMultiDemoAnalysis>['aiPromptMutation'];
	// New hierarchical analysis props
	analysisPhase?: AnalysisPhase;
	currentDemoIndex?: number;
	totalDemos?: number;
	individualAnalyses?: IndividualAnalysis[];
}

export const DemoAnalysisModal: React.FC<DemoAnalysisModalProps> = ({
	isOpen,
	onClose,
	isLoading,
	isCreatingConversation,
	onSendFollowUp,
	conversationId,
	aiPromptMutation,
	analysisPhase = 'idle',
	currentDemoIndex = 0,
	totalDemos = 0,
	individualAnalyses = [],
}) => {
	const conversationChatMessageQuery = useAIConversationChatMessageQuery({
		id: conversationId,
		enabled: Boolean(conversationId),
		staleTime: Infinity,
	});
	const [followUpQuestion, setFollowUpQuestion] = React.useState('');
	const [modalDragging, setModalDragging] = React.useState<{
		active?: boolean;
		x?: number;
		y?: number;
	}>({ active: false, x: 0, y: 0 });

	// Load modal position from localStorage on component mount
	React.useEffect(() => {
		try {
			const savedPosition = localStorage.getItem('demoAnalysisModalPosition');
			if (savedPosition) {
				const { x, y } = JSON.parse(savedPosition);
				setModalDragging(prev => ({ ...prev, x, y }));
			}
		} catch (error) {
			console.error('Error loading demo analysis modal position from localStorage:', error);
		}
	}, []);

	const onModalDrag = (event: DraggableEvent, { x, y }: DraggableData) => {
		event.stopPropagation();
		setModalDragging({ active: true, x, y });
	};

	const onModalDragStop = (_: DraggableEvent, { x, y }: DraggableData) => {
		const { active } = modalDragging;
		if (active) {
			setModalDragging({ active: false, x, y });

			// Save position to localStorage when drag stops
			try {
				localStorage.setItem('demoAnalysisModalPosition', JSON.stringify({ x, y }));
			} catch (error) {
				console.error('Error saving demo analysis modal position to localStorage:', error);
			}
		}
	};

	const handleSendFollowUp = React.useCallback(() => {
		if (!followUpQuestion.trim()) return;

		onSendFollowUp(followUpQuestion);
		setFollowUpQuestion('');
	}, [followUpQuestion, onSendFollowUp]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendFollowUp();
		}
	};

	// Calculate progress percentage for the progress bar
	const completedAnalysesCount = individualAnalyses.filter(a => a.status === 'complete').length;
	const progressPercentage = totalDemos > 0 ? Math.round((completedAnalysesCount / totalDemos) * 100) : 0;

	// Show individual analyses when we have any analyses (during or after individual phase)
	const hasIndividualAnalyses = individualAnalyses.length > 0;
	// Show expandable section when in synthesis/complete phase and we have individual analyses
	const showExpandableIndividualAnalyses =
		(analysisPhase === 'synthesis' || analysisPhase === 'complete') && individualAnalyses.length > 0;

	if (!isOpen) return null;

	return (
		<AidaDraggable
			onDrag={onModalDrag}
			onStop={onModalDragStop}
			defaultPosition={{ x: modalDragging.x, y: modalDragging.y }}
			position={{ x: modalDragging.x, y: modalDragging.y }}
			handle='.demo-analysis-drag-handle'
			isDraggable={true}
		>
			<div className={css(styleSheet.modalContainer)}>
				<div className={css(styleSheet.modalHeader)}>
					<div className={css(styleSheet.modalHeaderContent)}>
						<SparklesIcon className={css(styleSheet.modalIcon)} />
						<h3 className={css(styleSheet.modalTitle)}>Demo Analysis</h3>
					</div>
					<div className={css(styleSheet.modalHeaderActions)}>
						<div className='demo-analysis-drag-handle'>
							<DraggableIcon fill={brandPrimary} />
						</div>
						<button onClick={onClose} className={css(styleSheet.iconButton)}>
							<XIcon color={charmGray} height={12} width={12} />
						</button>
					</div>
				</div>
				<div className={css(styleSheet.modalContent)}>
					{/* Phase: Idle/Loading - waiting for data or initial setup */}
					{analysisPhase === 'idle' && !isCreatingConversation ? (
						<div className={css(styleSheet.loadingContainerWithColor)}>
							<LoadingSpinner type='small' />
							<div className={css(styleSheet.loadingText)}>Loading transcripts...</div>
						</div>
					) : null}

					{/* Phase: Creating conversation */}
					{isCreatingConversation && analysisPhase !== 'individual' ? (
						<div className={css(styleSheet.conversationContainer)}>
							{/* Show individual analyses while creating synthesis conversation */}
							{showExpandableIndividualAnalyses ? (
								<div className={css(styleSheet.synthesisAnalysesSection)}>
									<CollapsibleSection
										title={`Individual Demo Analyses (${individualAnalyses.length})`}
										defaultExpanded={true}
									>
										{individualAnalyses.map((analysis, index) => (
											<CollapsibleAnalysisItem
												key={analysis.demoId}
												analysis={analysis}
												index={index}
												defaultExpanded={false}
											/>
										))}
									</CollapsibleSection>
								</div>
							) : null}
							<div className={css(styleSheet.loadingContainerWithColor)}>
								<LoadingSpinner type='small' />
								<div className={css(styleSheet.loadingText)}>
									{analysisPhase === 'synthesis' ? 'Preparing synthesis...' : 'Creating AI Conversation...'}
								</div>
							</div>
						</div>
					) : null}

					{/* Phase: Individual analysis in progress */}
					{analysisPhase === 'individual' ? (
						<div className={css(styleSheet.individualPhaseContainer)}>
							<div className={css(styleSheet.progressContainer)}>
								<LoadingSpinner type='small' />
								<div className={css(styleSheet.progressTextContainer)}>
									<div className={css(styleSheet.loadingText)}>
										Analyzing demo {currentDemoIndex + 1} of {totalDemos}...
									</div>
									<div className={css(styleSheet.progressSubtext)}>
										Each demo is analyzed individually for thorough feedback
									</div>
								</div>
								<div className={css(styleSheet.progressBarContainer)}>
									<div className={css(styleSheet.progressBar)} style={{ width: `${progressPercentage}%` }} />
								</div>
								<div className={css(styleSheet.progressPercentage)}>
									{completedAnalysesCount} of {totalDemos} complete
								</div>
							</div>

							{/* Show completed individual analyses as they finish */}
							{hasIndividualAnalyses && completedAnalysesCount > 0 ? (
								<div className={css(styleSheet.individualAnalysesPreview)}>
									<CollapsibleSection title={`Completed Analyses (${completedAnalysesCount})`} defaultExpanded={true}>
										{individualAnalyses
											.filter(a => a.status === 'complete' || a.status === 'error')
											.map(analysis => (
												<CollapsibleAnalysisItem
													key={analysis.demoId}
													analysis={analysis}
													index={individualAnalyses.indexOf(analysis)}
													defaultExpanded={false}
												/>
											))}
									</CollapsibleSection>
								</div>
							) : null}
						</div>
					) : null}

					{/* Phase: Synthesis in progress or complete - show conversation */}
					{(analysisPhase === 'synthesis' || analysisPhase === 'complete') && !isCreatingConversation ? (
						<>
							<div className={css(styleSheet.conversationContainer)}>
								{/* Show individual analyses at top during synthesis phase */}
								{showExpandableIndividualAnalyses ? (
									<div className={css(styleSheet.synthesisAnalysesSection)}>
										<CollapsibleSection
											title={`Individual Demo Analyses (${individualAnalyses.length})`}
											defaultExpanded={analysisPhase === 'synthesis'}
										>
											{individualAnalyses.map((analysis, index) => (
												<CollapsibleAnalysisItem
													key={analysis.demoId}
													analysis={analysis}
													index={index}
													defaultExpanded={false}
												/>
											))}
										</CollapsibleSection>
									</div>
								) : null}

								{conversationChatMessageQuery.data?.map((message, index) => {
									return (
										<div
											key={index}
											className={css(
												styleSheet.messageContainer,
												message.role === AIChatRole.User ? styleSheet.userMessage : styleSheet.assistantMessage
											)}
										>
											<div className={css(styleSheet.messageRole)}>
												{message.role === AIChatRole.User ? 'You' : 'AI Assistant'}
											</div>
											<div className={css(styleSheet.messageContent)}>
												<AIMarkdownResponse>{message.text}</AIMarkdownResponse>
											</div>
										</div>
									);
								})}
								{aiPromptMutation.isLoading ? (
									<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
										<div className={css(styleSheet.messageRole)}>AI Assistant</div>
										<div className={css(styleSheet.loadingContainer)}>
											<LoadingSpinner type='small' />
											<div className={css(styleSheet.loadingText)}>
												{analysisPhase === 'synthesis' ? 'Synthesizing analyses...' : 'Thinking...'}
											</div>
										</div>
									</div>
								) : null}
							</div>

							{/* Follow-up question input - only show when not loading */}
							{!isLoading && analysisPhase === 'complete' && (
								<div className={css(styleSheet.inputContainer)}>
									<textarea
										className={css(styleSheet.textarea)}
										value={followUpQuestion}
										onChange={e => setFollowUpQuestion(e.target.value)}
										onKeyPress={handleKeyPress}
										placeholder={`Ask a follow-up question about the demo analysis...`}
										disabled={isLoading}
										rows={3}
									/>
									<div className={css(styleSheet.buttonContainer)}>
										<Button
											onClick={handleSendFollowUp}
											disabled={
												isLoading ||
												aiPromptMutation.isLoading ||
												aiPromptMutation.isStreaming ||
												!followUpQuestion.trim()
											}
										>
											Send Question
										</Button>
									</div>
								</div>
							)}
						</>
					) : null}
				</div>
			</div>
		</AidaDraggable>
	);
};
